import {
  EntityValueType,
  EntityValueTypeRanges,
} from '../../../../common/types/shared/entitySelection';

export interface CriterionInputConfig {
  type: string;
  helperText: string;
  inputProps?: Record<string, any>;
  validate?: (value: string) => string | null;
}

export const getCriterionInputConfig = (
  valueType: EntityValueType,
  comparator: string,
): CriterionInputConfig => {
  const range = EntityValueTypeRanges[valueType];
  const rangeText = range ? ` (${range.min} to ${range.max})` : '';

  const baseConfig: CriterionInputConfig = {
    type: 'text',
    helperText: `Enter ${valueType.toLowerCase()} value${rangeText}`,
  };

  switch (valueType) {
    case EntityValueType.NUMBER:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter a number',
        inputProps: {
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          return null;
        },
      };
    case EntityValueType.PERCENTAGE:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter a percentage (0-100)',
        inputProps: {
          min: 0,
          max: 100,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0 || num > 100) {
            return 'Percentage must be between 0 and 100';
          }
          return null;
        },
      };
    case EntityValueType.TEMPERATURE:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter temperature in Celsius (-273.15 to 1000)',
        inputProps: {
          min: -273.15,
          max: 1000,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < -273.15 || num > 1000) {
            return 'Temperature must be between -273.15 and 1000';
          }
          return null;
        },
      };
    case EntityValueType.HUMIDITY:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter humidity percentage (0-100)',
        inputProps: {
          min: 0,
          max: 100,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0 || num > 100) {
            return 'Humidity must be between 0 and 100';
          }
          return null;
        },
      };
    case EntityValueType.PRESSURE:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter pressure value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Pressure must be positive';
          }
          return null;
        },
      };
    case EntityValueType.VOLTAGE:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter voltage value',
        inputProps: {
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          return null;
        },
      };
    case EntityValueType.CURRENT:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter current value',
        inputProps: {
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          return null;
        },
      };
    case EntityValueType.POWER:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter power value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Power must be positive';
          }
          return null;
        },
      };
    case EntityValueType.ENERGY:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter energy value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Energy must be positive';
          }
          return null;
        },
      };
    case EntityValueType.FLOW_RATE:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter flow rate value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Flow rate must be positive';
          }
          return null;
        },
      };
    case EntityValueType.CONCENTRATION:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter concentration value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Concentration must be positive';
          }
          return null;
        },
      };
    case EntityValueType.LEVEL:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter level value (0-100)',
        inputProps: {
          min: 0,
          max: 100,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0 || num > 100) {
            return 'Level must be between 0 and 100';
          }
          return null;
        },
      };
    case EntityValueType.SPEED:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter speed value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Speed must be positive';
          }
          return null;
        },
      };
    case EntityValueType.FREQUENCY:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter frequency value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Frequency must be positive';
          }
          return null;
        },
      };
    case EntityValueType.AIRFLOW:
      return {
        ...baseConfig,
        type: 'number',
        helperText: 'Enter airflow value (must be positive)',
        inputProps: {
          min: 0,
          step: 'any',
        },
        validate: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Value must be a number';
          if (num < 0) {
            return 'Airflow must be positive';
          }
          return null;
        },
      };
    case EntityValueType.BOOLEAN:
      return {
        ...baseConfig,
        type: 'text',
        helperText: 'Enter true or false',
        validate: (value) => {
          if (value !== 'true' && value !== 'false') {
            return 'Value must be true or false';
          }
          return null;
        },
      };
    case EntityValueType.DATE:
      return {
        ...baseConfig,
        type: 'date',
        helperText: 'Select a date',
        validate: (value) => {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return 'Value must be a valid date';
          }
          return null;
        },
      };
    case EntityValueType.DATETIME:
      return {
        ...baseConfig,
        type: 'datetime-local',
        helperText: 'Select a date and time',
        validate: (value) => {
          const date = new Date(value);
          if (isNaN(date.getTime())) {
            return 'Value must be a valid date and time';
          }
          return null;
        },
      };
    case EntityValueType.STRING:
      return {
        ...baseConfig,
        type: 'text',
        helperText: 'Enter text',
        validate: (value) => {
          if (typeof value !== 'string') {
            return 'Value must be text';
          }
          return null;
        },
      };
    default:
      return baseConfig;
  }
};
